import { MutationTuple, useApolloClient, useMutation } from '@apollo/client';

import {
  DeleteRdProjectMutation,
  DeleteRdProjectMutationVariables,
  DELETE_RD_PROJECT,
  evictCache,
  GetRdProjectsQueryVariables,
  GET_RD_PROJECTS,
  getOperationName,
} from '@neo.tax/shared';

import { useRdContext } from '../context';

type UseDeleteRdProjectProps = {
  onCompleted?: () => void;
  rdProjectId: string;
};

export const useDeleteRdProject = ({
  onCompleted,
  rdProjectId,
}: UseDeleteRdProjectProps): MutationTuple<DeleteRdProjectMutation, DeleteRdProjectMutationVariables> => {
  const { fiscalYearId } = useRdContext();
  const client = useApolloClient();

  return useMutation<DeleteRdProjectMutation, DeleteRdProjectMutationVariables>(DELETE_RD_PROJECT, {
    context: {
      successToast: { title: 'Project deleted successfully' },
    },
    variables: { rdProjectId },
    refetchQueries: () => {
      const map = client.getObservableQueries();
      return Array.from(map.values())
        .filter((oq) => {
          const name = getOperationName(oq.query);
          const isGetRdProjects = name === 'GetRdProjects' || oq.query === GET_RD_PROJECTS;
          if (!isGetRdProjects) return false;
          const vars = oq.variables as GetRdProjectsQueryVariables | undefined;
          return vars?.fiscalYearId === fiscalYearId;
        })
        .map((oq) => ({ query: GET_RD_PROJECTS, variables: oq.variables }));
    },
    awaitRefetchQueries: false,
    update: (cache) => {
      cache.evict({ id: cache.identify({ __typename: 'RdProject', id: rdProjectId }) });
      evictCache(cache, ['getRdProjectTeamMembers', 'getRdProjectTpTickets']);
      cache.gc();
    },
    onCompleted,
  });
};
